import { DatasheetElement, DatasheetElementType } from "../types/datasheet-element";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { generateId } from "@common/utils/common";
import { FieldType } from "@common/dto/field/type";
import type { Field, TextItem } from "../components/editor/types/api-payload";

type ApiFieldResponse = Partial<Field> & Partial<DatasheetFieldComponentGetDto> & {
  headingPds?: string;
  headStyle?: number;
  headAlign?: number;
  headPos?: number;
  headFont?: string;
  headFontSize?: number;
  headColor?: number;
};

type ApiTextResponse = Partial<TextItem> & Partial<DatasheetTextComponentGetDto> & {
  textStyle?: number;
  fontSize?: number;
  fontName?: string;
  fontColor?: number;
  hexColor?: string;
  idKey?: number;
  tabId?: number;
};

type TabDataItem = { fieldId?: number; [key: string]: any };

export type TabLike = {
  id: string | number;
  fields?: Array<ApiFieldResponse>;
  texts?: Array<ApiTextResponse>;
  data?: Array<TabDataItem>;
};

export type DatasheetElementsByTab = Record<string, Record<string, DatasheetElement>>;

const convertApiNumberToAlign = (num: number | undefined): DatasheetFieldComponentTextAlign => {
  if (num === undefined || num === null) return DatasheetFieldComponentTextAlign.left;
  switch (num) {
    case 0:
      return DatasheetFieldComponentTextAlign.left;
    case 1:
      return DatasheetFieldComponentTextAlign.center;
    case 2:
      return DatasheetFieldComponentTextAlign.right;
    default:
      return DatasheetFieldComponentTextAlign.left;
  }
};

const convertApiNumberToHeadPos = (num: number | undefined): DatasheetFieldComponentHeadingPosition => {
  if (num === undefined || num === null) return DatasheetFieldComponentHeadingPosition.top;

  switch (num) {
    case 0:
      return DatasheetFieldComponentHeadingPosition.top;
    case 1:
      return DatasheetFieldComponentHeadingPosition.left;
    case 2:
      return DatasheetFieldComponentHeadingPosition.bottom;
    case 3:
    default:
      return DatasheetFieldComponentHeadingPosition.right;
  }
};

const convertNumberToTextStyleArray = (num: number | Array<DatasheetComponentTextStyle> | undefined): Array<DatasheetComponentTextStyle> => {
  if (Array.isArray(num)) return num;
  if (num === undefined || num === null || num === 0 || num === 33) return [];

  const styles: Array<DatasheetComponentTextStyle> = [];

  if ((num & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
    styles.push(DatasheetComponentTextStyle.bold);
  }
  if ((num & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
    styles.push(DatasheetComponentTextStyle.italic);
  }
  if ((num & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
    styles.push(DatasheetComponentTextStyle.underline);
  }

  return styles;
};

const getFieldTypeImageURL = (fieldType: number | FieldType | undefined): string => {
  switch (fieldType) {
    case FieldType.date:
      return "gif/date.gif";
    case FieldType.checkbox:
      return "gif/yesno.gif";
    case FieldType.dropdown:
      return "gif/dropdown.gif";
    case FieldType.hyperlink:
      return "gif/hyperlink.gif";
    case FieldType.image:
      return "gif/image2.gif";
    case FieldType.lookupTable:
      return "gif/lookup.gif";
    case FieldType.table:
      return "gif/table.gif";
    case FieldType.number:
      return "gif/numeric.gif";
    case FieldType.computed:
      return "gif/computed.gif";
    case FieldType.summary:
      return "gif/summary.gif";
    case FieldType.tableSummary:
      return "gif/tablesummary.gif";
    case FieldType.integer:
      return "gif/numeric.gif";
    case FieldType.text:
      return "gif/text.gif";
    default:
      return "gif/sysheadpeg.gif"
  }
};

/**
 * Builds datasheet elements grouped by tab so they can be pushed into local storage/state.
 */
export const buildDatasheetElementsByTab = (tabs: Array<TabLike>): DatasheetElementsByTab => {
  const allTexts: Array<{ text: ApiTextResponse; tabId: string | number }> = [];
  tabs.forEach((tab) => {
    (tab.texts || []).forEach((text) => {
      const textTabId = text.tabId;
      const resolvedTabId = textTabId !== undefined
        ? (typeof textTabId === 'number' ? textTabId : parseInt(String(textTabId), 10))
        : tab.id;
      allTexts.push({ text, tabId: resolvedTabId });
    });
  });

  return tabs.reduce<DatasheetElementsByTab>((acc, tab, tabIndex) => {
    const tabKey = String(tab.id);
    const elements: Record<string, DatasheetElement> = {};

    const dataMap = new Map<number, TabDataItem>();
    (tab.data || []).forEach((dataItem) => {
      if (dataItem.fieldId) {
        dataMap.set(dataItem.fieldId, dataItem);
      }
    });

    (tab.fields || []).forEach((field) => {
      const id = `field-${field.id ?? field.field?.fieldid ?? generateId()}`;

      if (!field.field) {
        console.warn("Field missing 'field' property:", field);
        const fallbackTabIdNum = typeof tab.id === "number" ? tab.id : parseInt(String(tab.id), 10);
        const fallbackTabid = (field as { tabid?: number; tabId?: number }).tabid
          ?? (field as { tabid?: number; tabId?: number }).tabId
          ?? fallbackTabIdNum;
        elements[id] = {
          id,
          type: DatasheetElementType.field,
          original: {
            ...field,
            tabid: fallbackTabid,
            headfont: field.headfont ?? 'Tahoma',
            headfontsize: field.headfontsize ?? 8,
          } as DatasheetFieldComponentGetDto,
          width: field.width ?? 0,
          height: field.height ?? 0,
          coordinates: {
            x: field.x ?? 0,
            y: field.y ?? 0,
          },
        };
        return;
      }

      const fieldId = field.field.fieldid;
      const fieldData = fieldId ? dataMap.get(fieldId) : null;

      const fieldType = field.field.fieldtype?.typeid ?? field.field.type ?? field.field.rawFieldType;
      const explicitDsSubfields = (field as any).dssubfields;
      const legacyDsSubfields = (field as any).m_ds_subfields;
      const hasExplicitDsSubfields = Object.prototype.hasOwnProperty.call(field as any, "dssubfields");
      const dsSubfields = hasExplicitDsSubfields
        ? explicitDsSubfields
        : (Array.isArray(legacyDsSubfields) && legacyDsSubfields.length > 0 ? legacyDsSubfields : undefined);
      const directSubfields = (field as any).subfields;
      const hasDsSubfields = Array.isArray(dsSubfields) && dsSubfields.length > 0;
      const hasDirectSubfields = Array.isArray(directSubfields) && directSubfields.length > 0;

      let normalizedSubfields: any[] | undefined = field.field.subfields;
      let componentSubfields: Array<{ field: any; width: number; [key: string]: any }> | undefined;

      if ((!field.field.subfields || field.field.subfields.length === 0) && (hasDsSubfields || hasDirectSubfields)) {
        const sourceSubfields = hasDsSubfields ? dsSubfields : directSubfields;
        const originList: any[] = [];
        const componentList: Array<{ field: any; width: number; [key: string]: any }> = [];

        (sourceSubfields as any[]).forEach((subfield: any) => {
          if (!subfield) return;
          const baseField = subfield.field ?? subfield;
          if (!baseField) return;

          const merged = {
            ...baseField,
            fontname: subfield.fontname ?? subfield.fontName ?? subfield.styleFontName ?? (baseField as any).fontname,
            fontsize: subfield.fontsize ?? subfield.fontSize ?? subfield.styleFontSize ?? (baseField as any).fontsize,
            fontcolor: typeof subfield.fontcolor === 'number'
              ? subfield.fontcolor
              : (typeof subfield.fontColor === 'number' ? subfield.fontColor : (baseField as any).fontcolor),
            textstyle: subfield.textstyle ?? subfield.textStyle ?? (baseField as any).textstyle,
            contentalign: subfield.contentalign ?? subfield.contentAlign ?? (baseField as any).contentalign,
          };

          originList.push(merged);
          componentList.push({
            ...subfield,
            field: merged,
            width: subfield.width ?? (merged as any).defaultWidth ?? 100,
          });
        });

        normalizedSubfields = originList;
        componentSubfields = componentList;
      } else if (Array.isArray(dsSubfields) && fieldType === FieldType.table) {
        componentSubfields = dsSubfields as Array<{ field: any; width: number; [key: string]: any }>;
      }

      const existingValue = (field.field as any).value;
      const fieldValue = fieldData
        ? (fieldType === FieldType.date
            ? (fieldData.rawValue ?? fieldData.dataValue ?? fieldData.displayValue ?? existingValue)
            : (fieldData.displayValue ?? fieldData.rawValue ?? fieldData.dataValue ?? existingValue))
        : existingValue;
      const originalImageURL = field.field.imageURL;
      const imageURL = originalImageURL || getFieldTypeImageURL(fieldType);

      const headingpds = field.headingPds ?? field.headingpds ?? '';
      const showfieldtype = field.showfieldtype ?? field.showFieldType ?? true;
      const headtext = field.headtext ?? (headingpds ? headingpds : '');
      const textStyleNum = typeof field.textStyle === 'number' ? field.textStyle : (typeof field.textstyle === 'number' ? field.textstyle : 0);
      const headdisplay = field.headdisplay !== undefined
        ? field.headdisplay
        : ((textStyleNum & 1) === 1);
      const showborder = field.showborder !== undefined
        ? field.showborder
        : ((textStyleNum & 32) === 32);

      const headstyle = Array.isArray(field.headstyle)
        ? field.headstyle
        : convertNumberToTextStyleArray(field.headStyle ?? field.headstyle);
      const headalign = field.headalign ?? convertApiNumberToAlign(field.headAlign);
      const headpos = field.headpos ?? convertApiNumberToHeadPos(field.headPos);

      const headfont = field.headFont ?? field.headfont ?? 'Tahoma';
      const headfontsize = field.headFontSize ?? field.headfontsize ?? 8;
      const headcolor = field.headColor ?? field.headcolor ?? 0;
      const textstyle = Array.isArray(field.textstyle)
        ? field.textstyle
        : convertNumberToTextStyleArray(field.textStyle ?? field.textstyle);
      const contentalign = field.contentalign ?? convertApiNumberToAlign(field.contentAlign);
      const fontname = field.fontName ?? field.fontname ?? 'Arial';
      const fontsize = field.fontSize ?? field.fontsize ?? 10;
      const fontcolor = (() => {
        const color = field.fontColor ?? field.fontcolor;
        return typeof color === 'number' ? color : 0;
      })();
      const tabOrder = field.tabOrder ?? 0;
      const helptext = field.helptext ?? field.helpText ?? null;
      const resolvedTabIdNum = typeof tab.id === "number" ? tab.id : parseInt(String(tab.id), 10);
      const tabid = (field as { tabid?: number; tabId?: number }).tabid
        ?? (field as { tabid?: number; tabId?: number }).tabId
        ?? resolvedTabIdNum;

      const fieldWithImageURL: DatasheetFieldComponentGetDto = {
        ...field,
        tabid,
        headingpds: headingpds,
        showfieldtype: showfieldtype,
        headtext: headtext,
        headdisplay: headdisplay,
        headstyle: headstyle,
        headalign: headalign,
        headpos: headpos,
        headfont: headfont,
        headfontsize: headfontsize,
        headcolor: headcolor,
        textstyle: textstyle,
        contentalign: contentalign,
        fontname: fontname,
        fontsize: fontsize,
        fontcolor: fontcolor,
        tabOrder: tabOrder,
        helptext: helptext,
        showborder: showborder,
        ...(fieldType === FieldType.table && componentSubfields && {
          dssubfields: componentSubfields as any,
          m_ds_subfields: componentSubfields as any,
        }),
        displayValue: fieldValue,
        ...(fieldData?.rawValue && { rawValue: fieldData.rawValue }),
        field: {
          ...field.field,
          value: fieldValue,
          ...(fieldData?.tableData && { tableData: fieldData.tableData }),
          ...(fieldData?.lookupValues && { lookupValues: fieldData.lookupValues }),
          ...(fieldType === FieldType.table && normalizedSubfields && { subfields: normalizedSubfields }),
          imageURL: imageURL,
          fieldtype: field.field.fieldtype ? {
            ...field.field.fieldtype,
            imageURL: imageURL,
          } : field.field.fieldtype,
          systemField: field.field.system !== undefined
            ? field.field.system
            : (field.field.systemField !== undefined ? field.field.systemField : false),
        },
      } as DatasheetFieldComponentGetDto;


      elements[id] = {
        id,
        type: DatasheetElementType.field,
        original: fieldWithImageURL,
        width: field.width ?? 0,
        height: field.height ?? 0,
        coordinates: {
          x: field.x ?? 0,
          y: field.y ?? 0,
        },
      };
    });

    allTexts.forEach(({ text, tabId }) => {
      if (tabId === tab.id) {
        const id = `text-${text.idKey ?? text.id ?? generateId()}`;
        if (!elements[id]) {
          const textStyleValue = text.textStyle ?? text.textstyle;
          const textStyleArray = convertNumberToTextStyleArray(textStyleValue);

          const fontsize = text.fontSize ?? text.fontsize ?? 14;
          const fontname = text.fontName ?? text.fontname ?? 'Arial';
          const fontColor = text.fontColor ?? 0;
          const hexColor = text.hexColor ?? '';
          const alignSource: number | undefined =
            typeof text.textAlign === "number"
              ? text.textAlign
              : typeof text.align === "number"
                ? text.align
                : undefined;
          const align =
            alignSource !== undefined
              ? convertApiNumberToAlign(alignSource)
              : DatasheetFieldComponentTextAlign.left;

          elements[id] = {
            id,
            type: DatasheetElementType.text,
            original: {
              ...text,
              textstyle: textStyleArray,
              fontsize: fontsize,
              fontname: fontname,
              fontColor: fontColor,
              hexColor: hexColor,
              align: align,
            } as DatasheetTextComponentGetDto,
            width: text.width ?? 0,
            height: text.height ?? 0,
            coordinates: {
              x: text.x ?? 0,
              y: text.y ?? 0,
            },
          };
        }
      }
    });

    acc[tabKey] = elements;
    return acc;
  }, {} as DatasheetElementsByTab);
};
